import React from "react";
import { Label } from "@/ui/label";
import { Input } from "@/ui/input";
import { Button } from "@/ui/button";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Loader, GenerateNotification, buildNotification } from "smart-react";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../constants/eventDataTypes";
import { getDeviceRegistrationStatus } from "../../../SmartRF/DeviceEnrollment/Services/DeviceEnrollmentService";

type TermType = "handheld" | "vehicle";

type LocaleOption = {
  value: string | number;
  text: string;
};

type Props = {
  singleProfile: any;
  setShowQRModal: (v: boolean) => void;
};

const QRCodeModal: React.FC<Props> = ({ singleProfile, setShowQRModal }) => {
  const [devcod, setDevcod] = React.useState("");
  const [termID, setTermID] = React.useState("");
  const [selectedProfile, setSelectedProfile] = React.useState<any | null>(
    null,
  );
  const [dialogueLoading, setDialogueLoading] = React.useState(false);
  const [mtfTermType, setMtfTermType] = React.useState<{
    value: TermType;
    text: string;
  }>({
    value: "handheld",
    text: "Handheld",
  });
  const [localeData, setLocaleData] = React.useState<LocaleOption[]>([]);
  const [mtfDisplayWidth, setMtfDisplayWidth] = React.useState<number>(20);
  const [mtfDisplayHeight, setMtfDisplayHeight] = React.useState<number>(16);
  const [localeId, setLocaleId] = React.useState<LocaleOption | null>(null);
  const [qrData, setQRData] = React.useState<string>("");
  const [showBase64, setShowBase64] = React.useState<boolean>(false);
  const lastHandledSeqRef = React.useRef<number | null>(null);

  const handleLocaleIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const option =
      localeData.find((o) => String(o.value) === String(value)) || null;
    setLocaleId(option);
  };

  const handleMtfTermTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as TermType;
    const next = v === "vehicle" ? { w: 40, h: 8 } : { w: 20, h: 16 };
    setMtfTermType({
      value: v,
      text: v === "vehicle" ? "Vehicle" : "Handheld",
    });
    setMtfDisplayWidth(next.w);
    setMtfDisplayHeight(next.h);
  };

  const buildQRPayload = (profile: any, dev: string, term: string) => {
    const data = {
      profileSetupGUID: profile?.srfqrvm?.SRFProfilesSetupId,
      smartAppsURL: profile?.srfqrvm?.SmartAppURL,
      mocaURL: profile?.srfqrvm?.MOCAURL,
      mtfURL: profile?.srfqrvm?.MTFURL,
      devcod: dev,
      termId: term,
      mtfTermType: mtfTermType?.value,
      mtfDisplayWidth: mtfDisplayWidth,
      mtfDisplayhight: mtfDisplayHeight,
      localeId: (localeId as any)?.value,
      mtfVendorName: `${profile?.srfqrvm?.MTFVendorName}`,
    };
    const jsonStr = JSON.stringify(data);
    const base64Str = btoa(jsonStr);
    return base64Str;
  };

  const generateNextIds = (seqNum: number) => {
    const next = seqNum + 1;
    const numberPart = String(next).padStart(7, "0");
    const generated = `SM${numberPart}`;
    return { dev: generated, term: generated, seq: next };
  };

  const generateQR = async (profile: any, seqNumber: number | null = null) => {
    setDialogueLoading(true);
    try {
      const sequenceNumber =
        seqNumber != null
          ? seqNumber
          : parseInt(profile?.auto_devcod_seqnum ?? "0", 10) || 0;

      const ids = generateNextIds(sequenceNumber);
      setDevcod(ids.dev);
      setTermID(ids.term);

      const payload = buildQRPayload(profile, ids.dev, ids.term);
      setQRData(payload);
    } finally {
      setDialogueLoading(false);
    }
  };

  const checkAndUpdateQR = async (profileId: any) => {
    if (!selectedProfile) return;
    try {
      const response = await (getDeviceRegistrationStatus as any)({
        profileSetupId: profileId,
      });
      const currentSeqNumber = parseInt(response?.Payload, 10);
      if (Number.isNaN(currentSeqNumber)) return;

      // Avoid duplicate handling for the same sequence in successive polls
      if (lastHandledSeqRef.current === currentSeqNumber) return;

      // Extract numeric sequence from Term ID (e.g., "SM0000527" -> 527)
      const termSeq = parseInt(termID.match(/\d+/)?.[0] ?? "0", 10);

      // Only notify and advance when server sequence has moved forward
      if (termSeq < currentSeqNumber) {
        const currentStr = String(currentSeqNumber).padStart(7, "0");

        GenerateNotification(
          buildNotification({
            title: "Successfully Registered",
            description: `SM${currentStr} registered successfully!`,
            style: "success",
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );

        lastHandledSeqRef.current = currentSeqNumber;

        await new Promise((resolve) => setTimeout(resolve, 300)); // short debounce
        setSelectedProfile((prev: any | null) =>
          prev ? { ...prev, auto_devcod_seqnum: currentSeqNumber } : prev,
        );
        await generateQR(selectedProfile, currentSeqNumber);
      }
      // Else: no change; skip notification and regeneration
    } catch (error) {
      // Silently ignore to avoid spamming user; could surface as toast if desired
      // console.error("Error checking device registration status:", error);
    }
  };

  React.useEffect(() => {
    if (!singleProfile) return;
    setSelectedProfile(singleProfile);
    const locVal = singleProfile?.srfqrvm?.LocaleId;
    const options: LocaleOption[] = locVal
      ? [{ value: locVal, text: String(locVal) }]
      : [];
    setLocaleData(options);
    setLocaleId(options.length ? options[0] : null);
    void generateQR(singleProfile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleProfile]);

  React.useEffect(() => {
    if (!selectedProfile) return;
    const profileId = selectedProfile?.srfqrvm?.SRFProfilesSetupId;
    if (!profileId) return;
    const id = setInterval(() => {
      void checkAndUpdateQR(profileId);
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfile, termID]);

  React.useEffect(() => {
    if (selectedProfile) {
      void generateQR(selectedProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mtfTermType, localeId]);

  const qrImgUrl =
    qrData &&
    `https://api.qrserver.com/v1/create-qr-code/?size=270x270&data=${encodeURIComponent(qrData)}`;

  return (
    <div className="space-y-4">
      {dialogueLoading && (
        <div className="py-6 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {selectedProfile && (
        <div className="space-y-4">
          {/* QR + toggle */}
          <div className="rounded-md bg-white p-3 border border-border">
            <div
              className="relative w-full min-h-[300px] [perspective:1000px] cursor-pointer"
              onClick={() => setShowBase64((v) => !v)}
              title={!showBase64 ? "Click to view Base64" : "Click to view QR"}
            >
              <div
                className={cn(
                  "relative h-full w-full transition-transform duration-500 transform-gpu [transform-style:preserve-3d]",
                  showBase64
                    ? "[transform:rotateY(180deg)]"
                    : "[transform:rotateY(0deg)]",
                )}
              >
                {/* Front (QR) */}
                <div className="absolute inset-0 [backface-visibility:hidden] grid place-items-center">
                  {qrImgUrl ? (
                    <img
                      src={qrImgUrl}
                      alt="QR Code"
                      width={270}
                      height={270}
                      className="block"
                    />
                  ) : (
                    <div className="w-[270px] h-[270px] flex items-center justify-center text-muted-foreground">
                      No data
                    </div>
                  )}
                </div>

                {/* Back (Base64) */}
                <div className="absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]">
                  <Label className="text-xs text-muted-foreground">
                    Base64 Payload
                  </Label>
                  <textarea
                    value={qrData}
                    readOnly
                    rows={8}
                    className="w-full h-48 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono leading-[1.4] overflow-auto"
                  />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigator.clipboard.writeText(qrData);
                        GenerateNotification(
                          buildNotification({
                            title: "Copied",
                            description: "Base64 copied to clipboard.",
                            style: "success",
                          }),
                          NOTIFICATION_TYPES.APP,
                          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
                        );
                      }}
                    >
                      Copy Base64
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="border-t border-border my-4" />

          {/* Settings */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Terminal Type
              </Label>
              <Select
                value={mtfTermType.value}
                onValueChange={(v) => {
                  // Preserve existing logic that updates width/height
                  handleMtfTermTypeChange({
                    target: { value: v },
                  } as any);
                }}
              >
                <SelectTrigger aria-label="Terminal Type">
                  <SelectValue placeholder="Select terminal type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="handheld">Handheld</SelectItem>
                  <SelectItem value="vehicle">Vehicle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Locale ID</Label>
              <Select
                value={String((localeId as any)?.value ?? "")}
                onValueChange={(v) => {
                  handleLocaleIdChange({ target: { value: v } } as any);
                }}
              >
                <SelectTrigger aria-label="Locale ID">
                  <SelectValue placeholder="N/A" />
                </SelectTrigger>
                <SelectContent>
                  {localeData.length === 0 ? (
                    <SelectItem value="" disabled>
                      N/A
                    </SelectItem>
                  ) : (
                    localeData.map((o) => (
                      <SelectItem key={String(o.value)} value={String(o.value)}>
                        {o.text}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Device Display Width
              </Label>
              <Input
                value={String(mtfDisplayWidth)}
                readOnly
                disabled
                className="h-10 bg-background"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Device Display Height
              </Label>
              <Input
                value={String(mtfDisplayHeight)}
                readOnly
                disabled
                className="h-10 bg-background"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Devcod</Label>
              <Input value={devcod} readOnly className="h-10 bg-background" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Term ID</Label>
              <Input value={termID} readOnly className="h-10 bg-background" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeModal;
